"use client";

import { useEffect } from "react";
import { apiFetch, versioned } from "@/lib/api";

/**
 * Captures uncaught browser errors and unhandled promise rejections and
 * forwards them to the backend `/api/v1/client-log` endpoint, which writes
 * them to logs/client errors.log. This makes client-side crashes visible in
 * the server logs (they previously never reached the backend).
 */
export default function ClientErrorLogger() {
  useEffect(() => {
    const post = (
      level: string,
      message: string,
      stack?: string,
      extra?: Record<string, unknown>,
    ) => {
      try {
        apiFetch(versioned("/api/v1/client-log"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            level,
            message,
            stack,
            url:
              typeof window !== "undefined" ? window.location.href : undefined,
            userAgent:
              typeof navigator !== "undefined"
                ? navigator.userAgent
                : undefined,
            extra,
          }),
        }).catch(() => {});
      } catch {
        /* never let the logger itself break the app */
      }
    };

    const onError = (e: ErrorEvent) => {
      post("error", e.message || "Unknown error", e.error?.stack, {
        source: e.filename,
        line: e.lineno,
        col: e.colno,
      });
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason as
        | { message?: string; stack?: string }
        | undefined;
      post(
        "unhandledrejection",
        reason?.message || String(e.reason),
        reason?.stack,
      );
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
